import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, Plus, Trash2, X, Save, Calendar, PlayCircle, Info, Sparkles } from "lucide-react";
import AiAssistModal from "@/components/AiAssistModal";

const GOALS = ["hypertrophy", "strength", "endurance", "rehab", "general"];

function ExercisePicker({ open, onClose, onPick }) {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState(null);
    useEffect(() => {
        if (!open) return;
        api.get("/exercises", { params: q ? { q } : {} }).then(({ data }) => setItems(data));
    }, [open, q]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="exercise-picker">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <h3 className="font-serif text-2xl">Pick an exercise</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="p-4 border-b border-[var(--border)]">
                    <input className="input-dark" placeholder="SEARCH" value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="exercise-picker-search" />
                </div>
                <div className="flex-1 overflow-auto">
                    {items.map((ex) => (
                        <div key={ex.id} className="border-b border-[var(--border)]" data-testid={`ex-row-${ex.id}`}>
                            <div className="px-5 py-3 flex items-center justify-between gap-3">
                                <button onClick={() => onPick(ex)} className="text-left flex-1 min-w-0" data-testid={`pick-${ex.id}`}>
                                    <p className="font-medium truncate">{ex.name}</p>
                                    <p className="text-xs text-[var(--text-muted)] font-mono truncate">{ex.category} · {ex.primary_muscle || "—"} · {ex.equipment || "—"}</p>
                                </button>
                                <button
                                    onClick={() => setExpanded(expanded === ex.id ? null : ex.id)}
                                    className="text-[var(--text-muted)] hover:text-[var(--gold)] shrink-0"
                                    title="Show description"
                                    data-testid={`info-${ex.id}`}
                                >
                                    <Info size={14} />
                                </button>
                                {ex.video_url && (
                                    <a href={ex.video_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[var(--gold)] hover:text-[var(--gold-bright)] shrink-0" title="Watch demo" data-testid={`demo-${ex.id}`}>
                                        <PlayCircle size={16} />
                                    </a>
                                )}
                                <button onClick={() => onPick(ex)} className="text-[var(--gold)] font-mono shrink-0">+</button>
                            </div>
                            {expanded === ex.id && (
                                <div className="px-5 pb-4 -mt-1 text-xs text-[var(--text-muted)] border-l-2 border-[var(--gold-deep)] ml-5" data-testid={`desc-${ex.id}`}>
                                    {ex.description || "Controlled tempo. Full range of motion."}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function AssignModal({ open, onClose, programId, programName, programDays = [], onAssigned }) {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(null);
    const [presetMsg, setPresetMsg] = useState("");

    useEffect(() => {
        if (open) api.get("/clients").then(({ data }) => { setClients(data); if (data[0]) setClientId(data[0].id); });
    }, [open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr(""); setDone(null);
        try {
            const { data } = await api.post(`/programs/${programId}/assign`, { client_id: clientId, start_date: start });
            setDone(data);
            onAssigned && onAssigned();
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const applyMwfPreset = async () => {
        // Pin first up-to-3 program days to Mon/Wed/Fri starting from the start_date week.
        if (!programDays.length) { setPresetMsg("Add training days first."); return; }
        const startD = new Date(start + "T00:00:00");
        const dayOfWeek = startD.getDay(); // 0=Sun, 1=Mon, …
        const offsetToMonday = (8 - dayOfWeek) % 7 || 7; // next Monday; if start is Mon → 7? we want next Mon
        // Better: find this week's Monday or next if start is past Monday
        const monday = new Date(startD);
        const back = (dayOfWeek + 6) % 7; // days since Monday
        monday.setDate(startD.getDate() - back);
        if (monday < startD) {
            // shift to next week if start_date is past Monday already
            monday.setDate(monday.getDate() + 7);
        }
        const mwf = [0, 2, 4].map((offset) => {
            const d = new Date(monday);
            d.setDate(monday.getDate() + offset);
            return d.toISOString().slice(0, 10);
        });
        const newDays = (programDays || []).map((d, i) => {
            if (i < 3) return { ...d, date: mwf[i] };
            return d; // beyond 3 days, leave as-is
        });
        try {
            await api.put(`/programs/${programId}`, {
                name: programName,
                description: "",
                weeks: 1,
                days_per_week: Math.min(programDays.length, 7),
                goal: "",
                days: newDays,
            });
            setPresetMsg(`Pinned ${Math.min(3, programDays.length)} days to Mon (${mwf[0]}) / Wed (${mwf[1]}) / Fri (${mwf[2]}).`);
        } catch (e) {
            setPresetMsg(formatApiError(e.response?.data?.detail) || "Preset failed");
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-program-modal">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <h3 className="font-serif text-2xl">Assign program</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                {done ? (
                    <div className="p-5 space-y-4">
                        <p className="text-[var(--gold-bright)] font-medium">Program assigned.</p>
                        <p className="text-sm text-[var(--text-muted)]">{done.workouts_created} workouts added to the calendar starting {start}.</p>
                        <div className="flex gap-3">
                            <Link to="/calendar" className="btn-primary flex-1 text-center"><Calendar size={12} className="inline mr-1" /> View calendar</Link>
                            <button onClick={onClose} className="btn-ghost">Close</button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-5 space-y-4">
                        <p className="text-sm text-[var(--text-muted)]">Schedule <span className="text-[var(--text)]">{programName}</span> for a client.</p>
                        <p className="text-xs text-[var(--text-faint)]">Days with a pinned date will use that exact date. Days without a pinned date use the start date below + their position in the week.</p>
                        <div>
                            <label className="label-tiny block mb-2">Client</label>
                            <select required className="input-dark" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="assign-program-client">
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Start date</label>
                            <input type="date" required className="input-dark" value={start} onChange={(e) => setStart(e.target.value)} data-testid="assign-program-start" />
                        </div>
                        <button type="button" onClick={applyMwfPreset} className="btn-ghost text-xs w-full" data-testid="mwf-preset-btn">
                            Pin first 3 days to Mon / Wed / Fri
                        </button>
                        {presetMsg && <p className="text-xs text-[var(--gold-bright)] font-mono">{presetMsg}</p>}
                        {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={busy} data-testid="confirm-assign-btn">
                                {busy ? "Assigning…" : "Assign program"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default function ProgramBuilderPage() {
    const { id } = useParams();
    const [params, setParams] = useSearchParams();
    const [program, setProgram] = useState(null);
    const [picker, setPicker] = useState({ open: false, dayIdx: null });
    const [assignOpen, setAssignOpen] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        api.get(`/programs/${id}`).then(({ data }) => setProgram(data));
        if (params.get("assign") === "1") setAssignOpen(true);
    }, [id]); // eslint-disable-line

    if (!program) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

    const update = (patch) => setProgram({ ...program, ...patch });

    const addDay = () => {
        const next = [...(program.days || []), {
            day: (program.days?.length || 0) + 1,
            title: `Day ${(program.days?.length || 0) + 1}`,
            type: "workout",
            exercises: [],
            notes: "",
        }];
        update({ days: next });
    };
    const updateDay = (idx, patch) => {
        const days = [...program.days];
        days[idx] = { ...days[idx], ...patch };
        update({ days });
    };
    const removeDay = (idx) => {
        const days = program.days.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day: i + 1 }));
        update({ days });
    };
    const addExercise = (dayIdx, exercise) => {
        const days = [...program.days];
        days[dayIdx].exercises.push({
            exercise_id: exercise.id,
            name: exercise.name,
            sets: 3, reps: "8-12", rest: "90s", tempo: "", notes: "",
        });
        update({ days });
    };
    const updateExercise = (dayIdx, exIdx, patch) => {
        const days = [...program.days];
        days[dayIdx].exercises[exIdx] = { ...days[dayIdx].exercises[exIdx], ...patch };
        update({ days });
    };
    const removeExercise = (dayIdx, exIdx) => {
        const days = [...program.days];
        days[dayIdx].exercises.splice(exIdx, 1);
        update({ days });
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                name: program.name,
                description: program.description || "",
                weeks: program.weeks,
                days_per_week: program.days_per_week,
                goal: program.goal,
                days: program.days || [],
            };
            await api.put(`/programs/${id}`, payload);
            setSavedAt(new Date());
        } finally { setSaving(false); }
    };

    const applyAi = (result) => {
        // Merge AI output into current program. Add new days, don't delete existing.
        const newDays = (result.days || []).map((d, i) => ({
            day: (program.days?.length || 0) + i + 1,
            title: d.title || `Day ${i + 1}`,
            type: d.type || "workout",
            exercises: (d.exercises || []).map((ex) => ({
                exercise_id: null,
                name: ex.name,
                sets: Number(ex.sets) || 3,
                reps: String(ex.reps || "8-12"),
                rest: String(ex.rest || "90s"),
                tempo: ex.tempo || "",
                notes: ex.notes || "",
            })),
            notes: d.notes || "",
        }));
        const merged = [...(program.days || []), ...newDays];
        update({
            days: merged,
            name: program.name || result.name,
            description: program.description || result.description || "",
            weeks: program.weeks || result.weeks || 4,
            days_per_week: program.days_per_week || result.days_per_week || Math.min(merged.length, 7),
        });
    };

    return (
        <div className="p-8 space-y-6" data-testid="program-builder-page">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <Link to="/programs" className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--gold)] mb-3"><ChevronLeft size={14} /> Programs</Link>
                    <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none font-serif text-5xl w-full max-w-2xl" value={program.name} onChange={(e) => update({ name: e.target.value })} data-testid="program-name-input" />
                    <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none text-sm text-[var(--text-muted)] mt-2 w-full max-w-2xl" placeholder="Add a description…" value={program.description || ""} onChange={(e) => update({ description: e.target.value })} />
                </div>
                <div className="flex items-center gap-3">
                    {savedAt && <span className="font-mono text-xs text-[var(--text-muted)]">saved {savedAt.toLocaleTimeString()}</span>}
                    <button onClick={() => setAiOpen(true)} className="btn-ghost inline-flex items-center gap-2 border-[var(--gold-deep)]" data-testid="program-ai-btn">
                        <Sparkles size={14} className="text-[var(--gold-bright)]" /> AI Assist
                    </button>
                    <button onClick={save} className="btn-ghost inline-flex items-center gap-2" disabled={saving} data-testid="save-program-btn">
                        <Save size={14} /> {saving ? "Saving…" : "Save"}
                    </button>
                    <button onClick={() => setAssignOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="open-assign-modal">
                        <Calendar size={14} /> Assign
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[var(--border)]">
                <div className="bg-[var(--surface)] p-4">
                    <p className="label-tiny mb-2">Weeks</p>
                    <input type="number" min={1} max={24} className="input-dark" value={program.weeks} onChange={(e) => update({ weeks: parseInt(e.target.value) || 1 })} />
                </div>
                <div className="bg-[var(--surface)] p-4">
                    <p className="label-tiny mb-2">Days / week</p>
                    <input type="number" min={1} max={7} className="input-dark" value={program.days_per_week} onChange={(e) => update({ days_per_week: parseInt(e.target.value) || 1 })} />
                </div>
                <div className="bg-[var(--surface)] p-4">
                    <p className="label-tiny mb-2">Goal</p>
                    <select className="input-dark" value={program.goal} onChange={(e) => update({ goal: e.target.value })}>
                        {GOALS.map((g) => <option key={g}>{g}</option>)}
                    </select>
                </div>
                <div className="bg-[var(--surface)] p-4">
                    <p className="label-tiny mb-2">Days defined</p>
                    <p className="font-display text-3xl">{(program.days || []).length}</p>
                </div>
            </div>

            <div className="space-y-4">
                {(program.days || []).map((d, idx) => (
                    <div key={idx} className="border border-[var(--border)] bg-[var(--surface)]" data-testid={`program-day-${idx}`}>
                        <div className="flex items-center justify-between p-4 border-b border-[var(--border)] flex-wrap gap-3">
                            <div className="flex items-center gap-4 flex-wrap">
                                <span className="font-mono text-xs text-[var(--gold)]">DAY {d.day}</span>
                                <input className="bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--gold)] outline-none font-display text-2xl tracking-[0.05em]" value={d.title} onChange={(e) => updateDay(idx, { title: e.target.value })} />
                                <select className="input-dark py-1 w-36 text-xs" value={d.type} onChange={(e) => updateDay(idx, { type: e.target.value })}>
                                    <option value="workout">Workout</option>
                                    <option value="session">1-on-1</option>
                                    <option value="recovery">Recovery</option>
                                </select>
                                <div className="flex items-center gap-2">
                                    <Calendar size={12} className="text-[var(--gold)]" />
                                    <input
                                        type="date"
                                        className="input-dark py-1 w-44 text-xs"
                                        value={d.date || ""}
                                        onChange={(e) => updateDay(idx, { date: e.target.value })}
                                        title="Pin a calendar date for this day (optional). Leave blank to use the start date when assigning."
                                        data-testid={`day-${idx}-date`}
                                    />
                                    {d.date && (
                                        <button onClick={() => updateDay(idx, { date: "" })} className="text-[var(--text-muted)] hover:text-[var(--danger)] text-xs" title="Clear pinned date" data-testid={`day-${idx}-date-clear`}>
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <button onClick={() => removeDay(idx)} className="text-[var(--text-muted)] hover:text-[var(--danger)]"><Trash2 size={14} /></button>
                        </div>
                        <div className="p-4 space-y-2">
                            {d.exercises.length === 0 && <p className="text-sm text-[var(--text-muted)]">No exercises yet.</p>}
                            {d.exercises.map((ex, exIdx) => (
                                <div key={exIdx} className="grid grid-cols-12 gap-2 items-center" data-testid={`day-${idx}-ex-${exIdx}`}>
                                    <div className="col-span-4 text-sm">{ex.name}</div>
                                    <input className="input-dark py-1.5 col-span-1 text-sm text-center" value={ex.sets} onChange={(e) => updateExercise(idx, exIdx, { sets: parseInt(e.target.value) || 1 })} />
                                    <input className="input-dark py-1.5 col-span-2 text-sm text-center" value={ex.reps} onChange={(e) => updateExercise(idx, exIdx, { reps: e.target.value })} />
                                    <input className="input-dark py-1.5 col-span-1 text-sm text-center" value={ex.rest} onChange={(e) => updateExercise(idx, exIdx, { rest: e.target.value })} />
                                    <input className="input-dark py-1.5 col-span-3 text-sm" placeholder="notes" value={ex.notes || ""} onChange={(e) => updateExercise(idx, exIdx, { notes: e.target.value })} />
                                    <button onClick={() => removeExercise(idx, exIdx)} className="col-span-1 text-[var(--text-muted)] hover:text-[var(--danger)] flex justify-center"><Trash2 size={14} /></button>
                                </div>
                            ))}
                            <div className="grid grid-cols-12 gap-2 text-[10px] tracking-[0.18em] uppercase text-[var(--text-faint)] mt-3">
                                <div className="col-span-4">Exercise</div>
                                <div className="col-span-1 text-center">Sets</div>
                                <div className="col-span-2 text-center">Reps</div>
                                <div className="col-span-1 text-center">Rest</div>
                                <div className="col-span-3">Notes</div>
                            </div>
                            <button onClick={() => setPicker({ open: true, dayIdx: idx })} className="btn-ghost mt-3 inline-flex items-center gap-1 text-xs" data-testid={`add-ex-day-${idx}`}>
                                <Plus size={12} /> Add exercise
                            </button>
                        </div>
                    </div>
                ))}
                <button onClick={addDay} className="w-full border border-dashed border-[var(--border-bright)] p-6 text-[var(--text-muted)] hover:text-[var(--gold)] hover:border-[var(--gold)] inline-flex items-center justify-center gap-2" data-testid="add-day-btn">
                    <Plus size={14} /> Add training day
                </button>
            </div>

            <ExercisePicker
                open={picker.open}
                onClose={() => setPicker({ open: false, dayIdx: null })}
                onPick={(ex) => { addExercise(picker.dayIdx, ex); setPicker({ open: false, dayIdx: null }); }}
            />
            <AssignModal
                open={assignOpen}
                onClose={() => { setAssignOpen(false); if (params.get("assign")) { params.delete("assign"); setParams(params); } }}
                programId={id}
                programName={program.name}
                programDays={program.days || []}
                onAssigned={() => api.get(`/programs/${id}`).then(({ data }) => setProgram(data))}
            />
            <AiAssistModal open={aiOpen} onClose={() => setAiOpen(false)} mode="workout" onApply={applyAi} />
        </div>
    );
}
